import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    email: '',
    role: 'user',
    status: 'active',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadUserDetails();
  }, [userId]);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found');
        }
        throw new Error(`Failed to load user details: ${response.statusText}`);
      }

      const data = await response.json();
      setUser(data.user);
      setEditForm({
        email: data.user.email,
        role: data.user.role,
        status: data.user.status,
        description: data.user.description || ''
      });

    } catch (err) {
      console.error('Error loading user details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserConversations = async (includeDeleted = false) => {
    try {
      setConversationsLoading(true);
      console.log('UserDetail: loadUserConversations called with includeDeleted:', includeDeleted);

      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/conversations?includeDeleted=${includeDeleted}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversations: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('UserDetail: received conversations data:', {
        totalConversations: data.conversations?.length || 0,
        deletedCount: data.deletedCount || 0,
        includeDeleted: data.includeDeleted
      });
      setConversations(data.conversations || []);
      setShowConversations(true);

    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err.message);
    } finally {
      setConversationsLoading(false);
    }
  };

  const restoreConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/conversations/${conversationId}/restore`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to restore conversation: ${response.statusText}`);
      }

      // Reload conversations to reflect the change
      await loadUserConversations(includeDeleted);
      
    } catch (err) {
      console.error('Error restoring conversation:', err);
      setError(err.message);
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editForm.email)) {
      setSaveError('Please enter a valid email address');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      console.log('Sending user update request:', editForm);
      
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(editForm)
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response data:', errorData);
        throw new Error(errorData.message || `Failed to update user: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Success response data:', data);
      setUser(data.user);
      setIsEditing(false);

    } catch (err) {
      console.error('Error updating user:', err);
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Are you sure you want to reset this user\'s password? A temporary password will be generated.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to reset password: ${response.statusText}`);
      }

      const data = await response.json();
      alert(`Password reset successful. Temporary password: ${data.temporaryPassword}\n\nPlease provide this to the user securely.`);

    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'suspended': return 'status-badge status-suspended';
      case 'inactive': return 'status-badge status-inactive';
      default: return 'status-badge';
    }
  };

  const getRoleBadgeClass = (role) => {
    return role === 'admin' ? 'role-badge role-admin' : 'role-badge role-user';
  };

  if (loading) {
    return (
      <div className="user-detail">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="user-detail">
        <div className="error-state">
          <h3>⚠️ Error Loading User</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadUserDetails} className="retry-button">
              🔄 Retry
            </button>
            <Link to="/admin/users" className="back-button">
              ← Back to Users
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail">
      <div className="user-detail-header">
        <div>
          <h2>👤 User Details</h2>
          <p>View and manage user account information</p>
        </div>
        <div className="header-actions">
          <Link to="/admin/users" className="back-button">
            ← Back to Users
          </Link>
          {!isEditing && (
            <button 
              className="edit-button"
              onClick={() => setIsEditing(true)}
            >
              ✏️ Edit User
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="user-detail-content">
        <div className="user-info-card">
          <h3>Account Information</h3>
          
          {isEditing ? (
            <form onSubmit={handleSaveChanges} className="edit-user-form">
              {saveError && (
                <div className="form-error">
                  ⚠️ {saveError}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  autoComplete="email"
                  inputMode="email"
                  spellCheck="false"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Admin Description</label>
                <textarea
                  id="description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description visible only to admins..."
                  rows="3"
                  maxLength="500"
                />
                <small className="form-note">
                  This description is only visible to administrators and can be used for internal notes about the user.
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    value={editForm.role}
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                    disabled={userId === currentUser?.userId}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  {userId === currentUser?.userId && (
                    <small className="form-note">You cannot change your own role</small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                    disabled={userId === currentUser?.userId}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  {userId === currentUser?.userId && (
                    <small className="form-note">You cannot change your own status</small>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditForm({
                      email: user.email,
                      role: user.role,
                      status: user.status,
                      description: user.description || ''
                    });
                    setSaveError(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="user-info-display">
              <div className="info-row">
                <label>Email:</label>
                <span>{user.email}</span>
              </div>
              
              <div className="info-row">
                <label>User ID:</label>
                <span className="user-id">{user.userId}</span>
              </div>
              
              <div className="info-row">
                <label>Role:</label>
                <span className={getRoleBadgeClass(user.role)}>
                  {user.role}
                </span>
              </div>
              
              <div className="info-row">
                <label>Status:</label>
                <span className={getStatusBadgeClass(user.status)}>
                  {user.status}
                </span>
              </div>
              
              <div className="info-row">
                <label>Admin Description:</label>
                <span className="user-description">
                  {user.description || <em>No description set</em>}
                </span>
              </div>
              
              <div className="info-row">
                <label>Created:</label>
                <span>{user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}</span>
              </div>
              
              <div className="info-row">
                <label>Last Login:</label>
                <span>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}</span>
              </div>
              
              <div className="info-row">
                <label>Login Count:</label>
                <span>{user.loginCount || 0}</span>
              </div>
              
              <div className="info-row">
                <label>Last IP:</label>
                <span>{user.lastLoginIP || 'N/A'}</span>
              </div>
            </div>
          )}
        </div>

        <div className="user-actions-card">
          <h3>Actions</h3>
          <div className="action-buttons">
            <button
              onClick={handleResetPassword}
              className="action-button reset-password-button"
              disabled={userId === currentUser?.userId}
            >
              🔑 Reset Password
            </button>
            
            <button
              onClick={() => {
                // Check if we're on mobile
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                  // Navigate to mobile conversation list
                  navigate(`/admin/users/${userId}/conversations${includeDeleted ? '?includeDeleted=true' : ''}`);
                } else {
                  // Load conversations inline for desktop
                  loadUserConversations(includeDeleted);
                }
              }}
              className="action-button view-chats-button"
              disabled={conversationsLoading}
            >
              {conversationsLoading ? '⏳ Loading...' : '💬 View Chat History'}
            </button>
          </div>
          
          {userId === currentUser?.userId && (
            <p className="action-note">
              ⚠️ Some actions are disabled because this is your own account
            </p>
          )}
        </div>

        {showConversations && (
          <div className="user-conversations-card">
            <div className="conversations-header">
              <h3>Chat History ({conversations.length} conversations)</h3>
              <div className="conversations-controls">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeDeleted}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      console.log('UserDetail: includeDeleted checkbox changed to:', newValue);
                      setIncludeDeleted(newValue);
                      loadUserConversations(newValue);
                    }}
                  />
                  <span className="checkbox-text">Include deleted conversations</span>
                </label>
              </div>
            </div>
            
            {conversations.length === 0 ? (
              <p className="empty-state">This user has no conversations yet.</p>
            ) : (
              <div className="conversations-list">
                {conversations.map(conversation => (
                  <div key={conversation.conversationId} className={`conversation-item ${conversation.deleted ? 'deleted' : ''}`}>
                    <div className="conversation-info">
                      <div className="conversation-title-row">
                        <h4>{conversation.title || 'Untitled Conversation'}</h4>
                        {conversation.deleted && (
                          <span className="deleted-badge">🗑️ DELETED</span>
                        )}
                      </div>
                      <div className="conversation-meta">
                        <span>Created: {new Date(conversation.createdAt).toLocaleString()}</span>
                        <span>Messages: {conversation.messageCount || 0}</span>
                        <span>Last Activity: {conversation.lastMessageAt ? new Date(conversation.lastMessageAt).toLocaleString() : 'N/A'}</span>
                        {conversation.deleted && conversation.deletedAt && (
                          <span className="deleted-info">Deleted: {new Date(conversation.deletedAt).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="conversation-actions">
                      <Link 
                        to={`/admin/users/${userId}/chats/${conversation.conversationId}${includeDeleted ? '?includeDeleted=true' : ''}`}
                        className="view-conversation-button"
                      >
                        👁️ View Messages
                      </Link>
                      {conversation.deleted && (
                        <button
                          onClick={() => restoreConversation(conversation.conversationId)}
                          className="restore-conversation-button"
                          title="Restore conversation"
                        >
                          🔄 Restore
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetail;